<!--
  Copyright (C) 2024 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
-->
<template>
  <PrimeDialog
    v-model:visible="dialogVisible"
    :header="$t('diffViewer.compareTitle')"
    modal
    maximizable
    class="diff-viewer-dialog"
    @update:visible="handleClose"
  >
    <div class="version-selectors">
      <div class="version-selector-group">
        <PrimeSelect
          v-model="selectedVersionA"
          :options="versionOptionsA"
          option-label="label"
          option-value="id"
          :placeholder="$t('diffViewer.selectVersionA')"
          class="flex-grow-1"
        >
          <template #value="slotProps">
            <div v-if="slotProps.value" class="flex align-items-center gap-2">
              <span>{{
                versionOptions.find((v) => v.id === slotProps.value)?.label
              }}</span>
              <PrimeBadge
                v-if="
                  versionOptions.find((v) => v.id === slotProps.value)
                    ?.isCurrent
                "
                :value="$t('diffViewer.currentVersion')"
                severity="info"
              />
            </div>
            <span v-else>{{ slotProps.placeholder }}</span>
          </template>
          <template #option="slotProps">
            <div class="flex align-items-center gap-2">
              <span>{{ slotProps.option.label }}</span>
              <PrimeBadge
                v-if="slotProps.option.isCurrent"
                :value="$t('diffViewer.currentVersion')"
                severity="info"
              />
            </div>
          </template>
        </PrimeSelect>
        <PrimeButton
          v-if="selectedVersionA && !isCurrentVersion(selectedVersionA)"
          type="button"
          icon="ri-history-line"
          :aria-label="
            $t('diffViewer.revertToThisVersion', {
              versionNumber: selectedVersionA,
            })
          "
          v-tooltip.top="
            $t('diffViewer.revertToThisVersion', {
              versionNumber: selectedVersionA,
            })
          "
          @click="revertVersion(selectedVersionA)"
          severity="secondary"
          rounded
          text
        />
      </div>
      <div class="version-selector-group">
        <PrimeSelect
          v-model="selectedVersionB"
          :options="versionOptionsB"
          option-label="label"
          option-value="id"
          :placeholder="$t('diffViewer.selectVersionB')"
          class="flex-grow-1"
        >
          <template #value="slotProps">
            <div v-if="slotProps.value" class="flex align-items-center gap-2">
              <span>{{
                versionOptions.find((v) => v.id === slotProps.value)?.label
              }}</span>
              <PrimeBadge
                v-if="
                  versionOptions.find((v) => v.id === slotProps.value)
                    ?.isCurrent
                "
                :value="$t('diffViewer.currentVersion')"
                severity="info"
              />
            </div>
            <span v-else>{{ slotProps.placeholder }}</span>
          </template>
          <template #option="slotProps">
            <div class="flex align-items-center gap-2">
              <span>{{ slotProps.option.label }}</span>
              <PrimeBadge
                v-if="slotProps.option.isCurrent"
                :value="$t('diffViewer.currentVersion')"
                severity="info"
              />
            </div>
          </template>
        </PrimeSelect>
        <PrimeButton
          v-if="selectedVersionB && !isCurrentVersion(selectedVersionB)"
          type="button"
          icon="ri-history-line"
          :aria-label="
            $t('diffViewer.revertToThisVersion', {
              versionNumber: selectedVersionB,
            })
          "
          v-tooltip.top="
            $t('diffViewer.revertToThisVersion', {
              versionNumber: selectedVersionB,
            })
          "
          @click="revertVersion(selectedVersionB)"
          severity="secondary"
          rounded
          text
        />
      </div>
    </div>

    <div v-if="selectedVersionA && selectedVersionB" class="diff-content">
      <div class="diff-headers">
        <div class="header-a">
          <strong>{{ $t("diffViewer.version") }} {{ selectedVersionA }}</strong>
        </div>
        <div class="header-b">
          <strong>{{ $t("diffViewer.version") }} {{ selectedVersionB }}</strong>
        </div>
      </div>

      <div class="diff-viewer">
        <div v-if="isLoading" class="loading-container">
          <PrimeProgressSpinner />
        </div>
        <div v-else-if="error" class="error-container">
          <i class="ri-error-warning-line mr-2 text-2xl" aria-hidden="true"></i>
          <p>{{ error }}</p>
        </div>
        <div v-else ref="mergeViewContainer" />
      </div>
    </div>
  </PrimeDialog>
</template>

<script setup>
import { ref, computed, watch, nextTick } from "vue";
import { useI18n } from "vue-i18n";
import { MergeView } from "@codemirror/merge";
import { EditorState } from "@codemirror/state";
import { lineNumbers } from "@codemirror/view";
import { json } from "@codemirror/lang-json";
import { xml } from "@codemirror/lang-xml";
import { markdown } from "@codemirror/lang-markdown";
import { html } from "@codemirror/lang-html";
import { yaml } from "@codemirror/lang-yaml";
import { oneDark } from "@codemirror/theme-one-dark";
import {
  syntaxHighlighting,
  defaultHighlightStyle,
} from "@codemirror/language";
import { useTheme } from "@/composables/useTheme.js";
import PrimeDialog from "primevue/dialog";
import PrimeSelect from "primevue/select";
import PrimeProgressSpinner from "primevue/progressspinner";
import PrimeBadge from "primevue/badge";
import PrimeButton from "primevue/button";
import { useToast } from "primevue/usetoast";
import alfrescoNodeService from "@/services/AlfrescoNodeService.js";

const { t } = useI18n();
const toast = useToast();
const { isDarkMode } = useTheme();

const props = defineProps({
  visible: {
    type: Boolean,
    required: true,
  },
  contentNode: {
    type: Object,
    required: true,
  },
  versions: {
    type: Array,
    default: () => [],
  },
  initialVersionId: {
    type: String,
    default: null,
  },
});

const emit = defineEmits(["update:visible", "revertVersion"]);

// --- Reactive State ---
const dialogVisible = ref(props.visible);
const selectedVersionA = ref(null);
const selectedVersionB = ref(null);
const contentA = ref("");
const contentB = ref("");
const isLoading = ref(false);
const error = ref(null);
const mergeViewContainer = ref(null);
const mergeViewInstance = ref(null);

// --- Computed Properties ---
const versionOptions = computed(() => {
  if (!props.versions) {
    return [];
  }
  const reversed = props.versions
    .map((v) => ({
      label: `${t("diffViewer.version")} ${
        v.entry.id
      } (${new Date(v.entry.modifiedAt).toLocaleDateString()})`,
      id: v.entry.id,
    }))
    .reverse();

  return reversed.map((v, index) => ({
    ...v,
    isCurrent: index === reversed.length - 1,
  }));
});

const versionOptionsA = computed(() => {
  return versionOptions.value.filter((v) => v.id !== selectedVersionB.value);
});

const versionOptionsB = computed(() => {
  return versionOptions.value.filter((v) => v.id !== selectedVersionA.value);
});

const languageExtension = computed(() => {
  const mimeType = props.contentNode?.content?.mimeType || "";
  switch (mimeType) {
    case "text/html":
      return [html()];
    case "text/markdown":
    case "text/x-markdown":
      return [markdown()];
    case "application/json":
      return [json()];
    case "application/xml":
    case "text/xml":
      return [xml()];
    case "application/x-yaml":
    case "text/yaml":
      return [yaml()];
    default:
      return [];
  }
});

// --- Methods ---
const handleClose = (value) => {
  dialogVisible.value = value;
  emit("update:visible", value);
};

const fetchContents = async () => {
  if (!selectedVersionA.value || !selectedVersionB.value) {
    return;
  }

  isLoading.value = true;
  error.value = null;

  try {
    const [blobA, blobB] = await Promise.all([
      alfrescoNodeService.getVersionContent(
        props.contentNode.id,
        selectedVersionA.value,
      ),
      alfrescoNodeService.getVersionContent(
        props.contentNode.id,
        selectedVersionB.value,
      ),
    ]);

    let textA = await blobA.text();
    let textB = await blobB.text();

    // Format JSON content
    if (props.contentNode?.content?.mimeType === "application/json") {
      try {
        textA = JSON.stringify(JSON.parse(textA), null, 2);
        textB = JSON.stringify(JSON.parse(textB), null, 2);
      } catch (e) {
        console.error("Failed to format JSON:", e);
      }
    }

    contentA.value = textA;
    contentB.value = textB;

    isLoading.value = false;
    await nextTick();
    initMergeView();
  } catch (err) {
    console.error("Error fetching versions:", err);
    error.value = t("diffViewer.errors.loadError");
    isLoading.value = false;
  }
};

const initMergeView = () => {
  if (mergeViewInstance.value) {
    mergeViewInstance.value.destroy();
    mergeViewInstance.value = null;
  }

  if (!mergeViewContainer.value) {
    return;
  }

  if (!contentA.value && !contentB.value) {
    return;
  }

  const themeExtension = isDarkMode()
    ? oneDark
    : syntaxHighlighting(defaultHighlightStyle, { fallback: true });
  const extensions = [
    lineNumbers(),
    ...languageExtension.value,
    themeExtension,
    EditorState.readOnly.of(true),
  ];

  try {
    mergeViewInstance.value = new MergeView({
      a: { doc: contentA.value, extensions },
      b: { doc: contentB.value, extensions },
      parent: mergeViewContainer.value,
    });
  } catch (e) {
    console.error("Error creating MergeView:", e);
    error.value = "Failed to create diff view: " + e.message;
  }
};

const initializeDefaultVersions = () => {
  if (versionOptions.value.length >= 2) {
    const length = versionOptions.value.length;
    if (
      props.initialVersionId &&
      versionOptions.value.some((v) => v.id === props.initialVersionId)
    ) {
      selectedVersionA.value = props.initialVersionId;
      selectedVersionB.value =
        versionOptions.value[length - 1]?.id === props.initialVersionId
          ? versionOptions.value[length - 2]?.id
          : versionOptions.value[length - 1]?.id;
    } else {
      // Pre-select the two most recent versions (at the end of the array)
      selectedVersionA.value = versionOptions.value[length - 2]?.id;
      selectedVersionB.value = versionOptions.value[length - 1]?.id;
    }
  }
};

const isCurrentVersion = (versionId) => {
  return versionOptions.value.find((v) => v.id === versionId)?.isCurrent;
};

const revertVersion = async (versionId) => {
  const comment = t("diffViewer.restoredVersionComment", {
    versionNumber: versionId,
  });

  try {
    await alfrescoNodeService.revertVersion(
      props.contentNode.id,
      versionId,
      comment,
    );

    emit("revertVersion");
    toast.add({
      severity: "success",
      summary: t("diffViewer.revertVersion.success"),
      detail: t("diffViewer.revertVersion.successDetail"),
      life: 3000,
    });

    // Fermer le dialog après la restauration
    handleClose(false);
  } catch (err) {
    console.error("Error reverting version:", err);
    toast.add({
      severity: "error",
      summary: t("diffViewer.revertVersion.error"),
      detail: err.message,
      life: 3000,
    });
  }
};

// --- Watchers ---
watch(
  () => props.visible,
  (newValue) => {
    dialogVisible.value = newValue;
    if (newValue) {
      initializeDefaultVersions();
    } else {
      // Clean up when dialog closes
      if (mergeViewInstance.value) {
        mergeViewInstance.value.destroy();
        mergeViewInstance.value = null;
      }
      selectedVersionA.value = null;
      selectedVersionB.value = null;
      contentA.value = "";
      contentB.value = "";
      error.value = null;
    }
  },
);

watch([selectedVersionA, selectedVersionB], () => {
  if (selectedVersionA.value && selectedVersionB.value) {
    fetchContents();
  }
});

watch(
  () => props.versions,
  (newVersions) => {
    if (newVersions && newVersions.length >= 2 && dialogVisible.value) {
      // Réinitialiser les versions par défaut quand la liste des versions change
      initializeDefaultVersions();
    }
  },
  { deep: true },
);
</script>

<style>
.diff-viewer-dialog.p-dialog:not(.p-dialog-maximized) {
  width: 85vw !important;
  max-width: 90vw !important;
  height: 85vh;
}
</style>

<style scoped>
.version-selectors {
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
  flex-shrink: 0;
}

.version-selector-group {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
}

.diff-content {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.diff-headers {
  display: grid;
  grid-template-columns: 1fr 1fr;
  text-align: center;
  padding: 0.5rem;
  background-color: var(--p-surface-hover);
  border: 1px solid var(--p-surface-border);
  border-bottom: none;
  border-radius: var(--p-border-radius) var(--p-border-radius) 0 0;
}

.diff-viewer {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  border: 1px solid var(--p-surface-border);
  border-radius: 0 0 var(--p-border-radius) var(--p-border-radius);
  overflow: auto;
}

.diff-viewer .cm-editor,
.diff-viewer .cm-merge-container {
  flex: 1;
  min-height: 0;
}

.loading-container,
.error-container {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2rem;
  min-height: 200px;
  text-align: center;
}

.error-container {
  color: var(--p-orange-500);
}

.diff-viewer-dialog .p-dialog-content {
  height: 100%;
  display: flex;
  flex-direction: column;
  padding-top: 1rem;
  overflow: hidden;
}

.diff-viewer .cm-scroller {
  overflow: auto;
}

.diff-viewer .cm-content {
  padding: 0.5rem 0;
}

.diff-viewer .cm-gutters {
  background-color: var(--surface-b);
  border-right: 1px solid var(--surface-d);
}

.diff-viewer .cm-lineNumbers .cm-gutterElement {
  padding: 0 0.5rem;
  color: var(--text-color-secondary);
}

.diff-viewer .cm-changedLine {
  background-color: rgba(28, 59, 82, 0.3);
}

.diff-viewer .cm-deletedLine {
  background-color: rgba(139, 0, 0, 0.2);
}

.diff-viewer .cm-insertedLine {
  background-color: rgba(0, 100, 0, 0.2);
}
</style>
